
import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContactForm from '../components/ContactForm';

const {FiMail, FiMapPin, FiClock, FiLinkedin, FiTwitter} = FiIcons;

const Contact = () => {
  const contactInfo = [
    {
      icon: FiMail,
      title: 'Email',
      details: 'hello@trbe.ai',
      description: 'Send us a message anytime'
    },
    {
      icon: FiMapPin,
      title: 'Location',
      details: 'Berlin, Germany',
      description: 'Available for in-person meetings'
    },
    {
      icon: FiClock,
      title: 'Hours',
      details: 'Mon-Fri 9:00-18:00',
      description: 'CET/CEST timezone'
    }
  ];

  const faqs = [
    {
      question: 'How do you find candidates?',
      answer: 'We use a combination of our extensive network, targeted sourcing, and industry partnerships to find the best talent in robotics, AI, and computer vision.'
    },
    {
      question: 'What are your fees?',
      answer: 'Our fees are competitive and transparent. We offer different engagement models depending on your needs, from contingency to retained search.'
    },
    {
      question: 'How long does the process take?',
      answer: 'On average, we can present qualified candidates within 2 weeks and complete placements within 4-6 weeks, depending on role complexity.'
    },
    {
      question: 'Do you work with startups?',
      answer: 'Absolutely! We specialize in working with startups and scale-ups, offering flexible engagement models that work for fast-growing companies.'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Let's Start Building Your Team
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Ready to find exceptional talent for your deep tech company? Get in touch for a free consultation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={info.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-2">{info.title}</h3>
                <p className="text-lg text-primary-600 font-medium mb-2">{info.details}</p>
                <p className="text-neutral-600">{info.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Send Us a Message
            </h2>
            <p className="text-xl text-neutral-600">
              Tell us about your hiring needs and we'll get back to you within 24 hours.
            </p>
          </motion.div>
          <ContactForm />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-neutral-600">
              Quick answers to common questions about our services.
            </p>
          </motion.div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-neutral-50 rounded-xl p-6"
              >
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">{faq.question}</h3>
                <p className="text-neutral-600">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Book a free consultation to discuss your hiring needs and learn how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://tidycal.com/trbe/exploration-call"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-primary-600 px-8 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors duration-200"
              >
                Schedule a Call
              </a>
              <div className="flex space-x-4 justify-center">
                <a href="#" className="text-primary-100 hover:text-white transition-colors">
                  <SafeIcon icon={FiLinkedin} className="h-6 w-6" />
                </a>
                <a href="#" className="text-primary-100 hover:text-white transition-colors">
                  <SafeIcon icon={FiTwitter} className="h-6 w-6" />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
